import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Play, Pause, Square, Trash2, RotateCcw, Plus, Bot, Clock, DollarSign, Zap,
} from 'lucide-react';
import {
  useInstances, useInstance,
  usePauseInstance, useResumeInstance, useStopInstance, useDeleteInstance, useSpawnInstance,
} from '../hooks/useInstances';
import { InstanceStatus, SpawnMode, type Instance } from '../types/instance';
import InstanceTerminal from '../components/InstanceTerminal';
import XTerminal from '../components/XTerminal';
import InstanceLaunchModal from '../components/InstanceLaunchModal';

const STATUS_CONFIG: Record<InstanceStatus, { color: string; bg: string; label: string }> = {
  [InstanceStatus.RUNNING]: { color: 'bg-green-500', bg: 'bg-green-50 text-green-700', label: 'Running' },
  [InstanceStatus.PAUSED]: { color: 'bg-yellow-500', bg: 'bg-yellow-50 text-yellow-700', label: 'Paused' },
  [InstanceStatus.STARTING]: { color: 'bg-blue-500', bg: 'bg-blue-50 text-blue-700', label: 'Starting' },
  [InstanceStatus.PENDING]: { color: 'bg-blue-400', bg: 'bg-blue-50 text-blue-700', label: 'Pending' },
  [InstanceStatus.COMPLETED]: { color: 'bg-slate-400', bg: 'bg-slate-100 text-slate-600', label: 'Completed' },
  [InstanceStatus.STOPPED]: { color: 'bg-slate-400', bg: 'bg-slate-100 text-slate-600', label: 'Stopped' },
  [InstanceStatus.FAILED]: { color: 'bg-red-500', bg: 'bg-red-50 text-red-700', label: 'Failed' },
};

type FilterStatus = 'all' | InstanceStatus;

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function InstanceCard({ instance, selected, onClick }: {
  instance: Instance;
  selected: boolean;
  onClick: () => void;
}) {
  const cfg = STATUS_CONFIG[instance.status];

  return (
    <button
      onClick={onClick}
      className={`w-full rounded-lg border p-3 text-left transition-colors ${
        selected
          ? 'border-slate-900 bg-slate-50'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-start gap-2">
        <div className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${cfg.color}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-slate-900">
              {instance.agent?.name ?? 'Unknown agent'}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-500">
            {instance.task?.title ?? 'Custom prompt'}
          </p>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-400">
            <span>{instance.model}</span>
            <span>Turn {instance.currentTurn}/{instance.maxTurns}</span>
            <span>${Number(instance.totalCostUsd).toFixed(2)}</span>
            <span>{relativeTime(instance.updatedAt)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function InstanceDetailPanel({ instanceId }: { instanceId: string; projectId: string }) {
  const { data: instance, isLoading } = useInstance(instanceId);
  const pauseInstance = usePauseInstance();
  const resumeInstance = useResumeInstance();
  const stopInstance = useStopInstance();
  const deleteInstance = useDeleteInstance();
  const spawnInstance = useSpawnInstance();

  if (isLoading) {
    return <div className="flex-1 p-6 text-sm text-slate-400">Loading instance...</div>;
  }

  if (!instance) {
    return <div className="flex-1 p-6 text-sm text-slate-500">Instance not found.</div>;
  }

  const cfg = STATUS_CONFIG[instance.status];

  const handlePause = () => pauseInstance.mutate(instance.id);
  const handleResume = () => resumeInstance.mutate({ id: instance.id });
  const handleStop = () => stopInstance.mutate(instance.id);
  const handleDelete = () => {
    if (confirm('Delete this instance? This cannot be undone.')) {
      deleteInstance.mutate(instance.id);
    }
  };
  const handleSpawn = () => spawnInstance.mutate(instance.id);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">
              {instance.agent?.name ?? 'Unknown agent'}
            </h2>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bg}`}>
              {cfg.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {instance.status === InstanceStatus.RUNNING && (
              <>
                <button
                  onClick={handlePause}
                  disabled={pauseInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Pause className="h-3.5 w-3.5" />
                  Pause
                </button>
                <button
                  onClick={handleStop}
                  disabled={stopInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop
                </button>
              </>
            )}
            {instance.status === InstanceStatus.PAUSED && (
              <>
                <button
                  onClick={handleResume}
                  disabled={resumeInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Play className="h-3.5 w-3.5" />
                  Resume
                </button>
                <button
                  onClick={handleStop}
                  disabled={stopInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop
                </button>
              </>
            )}
            {instance.status === InstanceStatus.PENDING && (
              <>
                <button
                  onClick={handleSpawn}
                  disabled={spawnInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Play className="h-3.5 w-3.5" />
                  Spawn
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </>
            )}
            {(instance.status === InstanceStatus.COMPLETED ||
              instance.status === InstanceStatus.FAILED ||
              instance.status === InstanceStatus.STOPPED) && (
              <>
                <button
                  onClick={handleDelete}
                  disabled={deleteInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
                <button
                  onClick={handleSpawn}
                  disabled={spawnInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Restart
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Bot className="h-3.5 w-3.5" />
            {instance.model}
          </span>
          <span className="flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" />
            Turns: {instance.currentTurn}/{instance.maxTurns}
          </span>
          <span className="flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5" />
            ${Number(instance.totalCostUsd).toFixed(2)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {instance.totalTokens.toLocaleString()} tokens
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {instance.spawnMode === SpawnMode.CLI ? (
          <XTerminal
            instanceId={instance.id}
            interactive={instance.status === InstanceStatus.RUNNING}
          />
        ) : (
          <InstanceTerminal instanceId={instance.id} status={instance.status} />
        )}
      </div>
    </div>
  );
}

export default function Instances() {
  const { id: projectId } = useParams<{ id: string }>();
  const { data: instances, isLoading } = useInstances(projectId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [showLaunchModal, setShowLaunchModal] = useState(false);

  const filteredInstances = instances?.filter((inst) =>
    filter === 'all' ? true : inst.status === filter,
  ) ?? [];

  return (
    <div className="flex h-full">
      {/* Left panel - instance list */}
      <div className="flex w-80 shrink-0 flex-col border-r border-slate-200">
        <div className="border-b border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Instances</h2>
            <button
              onClick={() => setShowLaunchModal(true)}
              className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
            >
              <Plus className="h-3.5 w-3.5" />
              Launch
            </button>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterStatus)}
            className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          >
            <option value="all">All statuses</option>
            <option value={InstanceStatus.RUNNING}>Running</option>
            <option value={InstanceStatus.PAUSED}>Paused</option>
            <option value={InstanceStatus.COMPLETED}>Completed</option>
            <option value={InstanceStatus.FAILED}>Failed</option>
            <option value={InstanceStatus.STOPPED}>Stopped</option>
            <option value={InstanceStatus.PENDING}>Pending</option>
            <option value={InstanceStatus.STARTING}>Starting</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading...</div>
          ) : filteredInstances.length === 0 ? (
            <div className="py-8 text-center">
              <Bot className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm text-slate-500">No instances</p>
              <p className="mt-1 text-xs text-slate-400">Launch one to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredInstances.map((inst) => (
                <InstanceCard
                  key={inst.id}
                  instance={inst}
                  selected={inst.id === selectedId}
                  onClick={() => setSelectedId(inst.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right panel - instance detail */}
      {selectedId ? (
        <InstanceDetailPanel instanceId={selectedId} projectId={projectId!} />
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <Bot className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">Select an instance to view details</p>
          </div>
        </div>
      )}

      <InstanceLaunchModal
        open={showLaunchModal}
        onClose={() => setShowLaunchModal(false)}
        projectId={projectId!}
      />
    </div>
  );
}
